"use client";

import dynamic from "next/dynamic";
import { createContext, useContext, useState, type FocusEvent, type PointerEvent } from "react";

import BirthToolFormShell, {
  type BirthToolFormShellProps,
} from "@/components/tool-shell/BirthToolFormShell";

import {
  UPAY_CHART_CHECK_FIELD_IDS,
  UPAY_CHART_CHECK_FORM,
  UPAY_CHART_CHECK_FRAME,
  UPAY_CHART_CHECK_HI_FORM,
  UPAY_CHART_FORM_RESERVE,
} from "./upay-chart-check-form";

// The /upay/ hub check's one boundary (the /graha/ hub's pattern,
// GrahaChartCheckLazy): the hub's section imports only this module, and it
// owns the one dynamic() to the island. The form, place search, kundali
// client, chart reader, answer and analytics load as an async chunk; the first
// load gains only this file and the shell.
//
// Activation is element-scoped and local: no window listener and no timer, so
// a reader scrolling the hub never downloads the island; a press, focus or
// submit on the shell does.
//
// The slot reserves the form's tallest state (UPAY_CHART_FORM_RESERVE), so
// neither shell -> form nor an engine error after a slow reply moves the guide
// below; the answer's taller room is added by the island in the press that
// sends the request (UPAY_CHART_ANSWER_RESERVE). Natural heights measured
// 2026-10-22 (next dev + Playwright on /upay/ and /hi/upay/, 7 widths from 320
// to 1280, service workers blocked): the empty form is 436 at 320-390 px wide
// and 362 from 640 (378 on the Hindi card); with "Don't know your birth time?"
// open and the field errors of an empty submit, 580 at 320-360, 564 at 390,
// 548 at 414, 532 at 480 and 518 from 640 (534 on the Hindi card). Hence the
// /graha/ floors, 600 px on phones and 540 from the sm breakpoint. Both submit
// labels stay on one line at 320 px; the first, longer ones wrapped and added
// 20 px. The tallest answer tested (all nine grahas listed, each debilitated,
// combust and in the 12th, under the longest sign name) is 2369 at 320, 2240
// at 360, 2219 at 390, 2198 at 414, 1936 at 480, 1630 at 640 and 1468 at 1280;
// the Hindi card is shorter at every width (1733 at 320, 1306 at 640). Hence
// 2400 px on phones and 1660 from sm. With POST /kundali mocked, the guide
// below moved only inside the submit press and recorded no layout shift
// outside the reader's input at 320, 390, 640 and 1280 on either page.
// The /hi/upay/ hub mounts the same slot with language="hi": the same form
// component with the Hinglish card and the same floors.
// translate="no" covers the shell, form and answer, which re-render on "Check
// another birth date" (tests/translate-safe-islands.test.ts).

type CheckLanguage = "en" | "hi";

/** Each locale's shell: the live form's words in the shared frame. */
const SHELL: Readonly<Record<CheckLanguage, BirthToolFormShellProps>> = {
  en: { ...UPAY_CHART_CHECK_FORM, className: UPAY_CHART_CHECK_FRAME },
  hi: { ...UPAY_CHART_CHECK_HI_FORM, className: UPAY_CHART_CHECK_FRAME },
};

/**
 * The shell's props for this page's language. dynamic()'s loading placeholder
 * receives no props from the page, so the slot hands them down through
 * context (GrahaChartCheckLazy).
 */
const ShellProps = createContext<BirthToolFormShellProps>(SHELL.en);

function LoadingShell() {
  return <BirthToolFormShell {...useContext(ShellProps)} />;
}

const UpayChartCheck = dynamic(() => import("./UpayChartCheck"), {
  ssr: false,
  loading: () => <LoadingShell />,
});

/** BirthToolFormShell input names -> the real form's input ids. */
const SHELL_FIELD_IDS: Readonly<Record<string, string>> = {
  dob: UPAY_CHART_CHECK_FIELD_IDS.date,
  tob: UPAY_CHART_CHECK_FIELD_IDS.time,
  place: UPAY_CHART_CHECK_FIELD_IDS.place,
};

function formFieldId(target: EventTarget | null): string {
  const name = target instanceof Element ? target.getAttribute("name") : null;
  return (name && SHELL_FIELD_IDS[name]) || UPAY_CHART_CHECK_FIELD_IDS.date;
}

export default function UpayChartCheckLazy({ language = "en" }: { language?: CheckLanguage }) {
  const [active, setActive] = useState(false);
  const [focusFieldId, setFocusFieldId] = useState<string | null>(null);
  const shell = SHELL[language];

  // Focus follows the visitor into the real form after a keyboard focus, a
  // mouse press or a submit, and never after a touch, which may start a scroll.
  const shellIntent = active
    ? {}
    : {
        onPointerDownCapture: (event: PointerEvent<HTMLDivElement>) =>
          setFocusFieldId(event.pointerType === "mouse" ? formFieldId(event.target) : null),
        onFocusCapture: (event: FocusEvent<HTMLDivElement>) =>
          setFocusFieldId(formFieldId(event.target)),
        onSubmitCapture: () => setFocusFieldId(UPAY_CHART_CHECK_FIELD_IDS.date),
      };

  return (
    <div
      translate="no"
      data-upay-hub-check-slot=""
      className={`flex flex-col ${UPAY_CHART_FORM_RESERVE}`}
      {...shellIntent}
    >
      {active ? (
        <ShellProps.Provider value={shell}>
          <UpayChartCheck language={language} focusFieldId={focusFieldId} />
        </ShellProps.Provider>
      ) : (
        <BirthToolFormShell {...shell} onActivate={() => setActive(true)} />
      )}
    </div>
  );
}
